/**
 * useTimeOnPage Hook
 * Tracks time spent on a page for engagement metrics
 *
 * @module hooks/useTimeOnPage
 * @description Fires engagement event after user spends 30+ seconds on page
 *
 * @example
 * ```tsx
 * // In a page component
 * useTimeOnPage();
 * ```
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackTimeOnPage } from "@/lib/analytics";

/** Time thresholds to track (in seconds) */
const TIME_THRESHOLDS = [30, 60, 120, 300];

export const useTimeOnPage = (): void => {
  const location = useLocation();
  const startTimeRef = useRef<number>(Date.now());
  const trackedThresholds = useRef<Set<number>>(new Set());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Reset on route change
    startTimeRef.current = Date.now();
    trackedThresholds.current.clear();

    const checkTimeSpent = () => {
      const secondsSpent = Math.floor(
        (Date.now() - startTimeRef.current) / 1000
      );

      TIME_THRESHOLDS.forEach((threshold) => {
        if (
          secondsSpent >= threshold &&
          !trackedThresholds.current.has(threshold)
        ) {
          trackedThresholds.current.add(threshold);
          trackTimeOnPage(threshold, location.pathname);
        }
      });
    };

    // Check every 10 seconds
    intervalRef.current = setInterval(checkTimeSpent, 10000);

    // Also track on page visibility change (user leaving tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        const secondsSpent = Math.floor(
          (Date.now() - startTimeRef.current) / 1000
        );
        if (secondsSpent >= 10) {
          trackTimeOnPage(secondsSpent, location.pathname);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [location.pathname]);
};

export default useTimeOnPage;
